import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AppButton, Box, RoundDoneIcon, Text } from '@common-fe/common-fe';

import ROUTES from '@/common/routes';
import { Modal } from '@/components/elements';
import { useHistory } from '@/modules/core/hooks';

interface Props {
  createdClaimId?: string;
  onSetVisible: (value: boolean) => void;
  onUploadFile: () => void;
  isClaimAutomaticallyApproved?: boolean;
  isClaimCreatedWithoutDocument?: boolean;
  isHsaWithdrawalClaim?: boolean;
}

const ClaimSuccessfullyCreatedPopup: React.FC<Props> = ({
  createdClaimId,
  onSetVisible,
  onUploadFile,
  isClaimAutomaticallyApproved,
  isClaimCreatedWithoutDocument,
  isHsaWithdrawalClaim,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const buttonClickedRef = useRef(false);

  const title = useMemo(() => {
    if (isClaimAutomaticallyApproved) {
      return t('Your claim has been approved!');
    };

    if (isClaimCreatedWithoutDocument) {
      return t('Claim saved, but a document required to process');
    };

    if (isHsaWithdrawalClaim) {
      return t('Your HSA Withdrawal has been submitted');
    }

    return t('Your request has been submitted for additional review');
  }, [t, isClaimAutomaticallyApproved, isClaimCreatedWithoutDocument, isHsaWithdrawalClaim]);

  const description = useMemo(() => {
    if (isClaimAutomaticallyApproved || isHsaWithdrawalClaim) {
      return (
        <>
          <Text>
            {t('You will receive your reimbursement within [1-3 business days].')}
          </Text>
          <br />
          <Text>
            {t('You can check the status of this claim under expense details.')}
          </Text>
        </>
      );
    };

    if (isClaimCreatedWithoutDocument) {
      return t('We’ve saved this claim to your Expenses list. To process it, please upload the required document.');
    };

    return t('You can check on the status of this request under expense details.');
  }, [t, isClaimAutomaticallyApproved, isClaimCreatedWithoutDocument, isHsaWithdrawalClaim]);

  const handleSubmitAnotherClaim = useCallback(() => {
    buttonClickedRef.current = true;
    history.push(ROUTES.USE_MY_MONEY);
    onSetVisible(false);
  }, [onSetVisible, history]);

  const handleGoToExpenseDetails = useCallback(() => {
    buttonClickedRef.current = true;
    history.push(ROUTES.EXPENSES_TRANSACTION_DETAILS_CLAIM(createdClaimId));
    onSetVisible(false);
  }, [onSetVisible, history, createdClaimId]);

  const handleUploadDocument = useCallback(() => {
    buttonClickedRef.current = true;
    onUploadFile();
    onSetVisible(false);
  }, [onUploadFile, onSetVisible]);

  const closeButton = useMemo(() => {
    if (isClaimCreatedWithoutDocument) {
      return (
        <AppButton
          testId="claim-successfully-created_popup_go-to-expense-details"
          width="100%"
          buttonType="secondary"
          onClick={handleGoToExpenseDetails}
        >
          {t('Go to Expense Details')}
        </AppButton>
      );
    };

    return (
      <AppButton
        testId="claim-successfully-created_popup_submit-another-claim"
        buttonType="secondary"
        width="100%"
        onClick={handleSubmitAnotherClaim}
      >
        {t('Submit Another Claim')}
      </AppButton>
    );
  }, [t, isClaimCreatedWithoutDocument, handleSubmitAnotherClaim, handleGoToExpenseDetails]);

  const submitButton = useMemo(() => {
    if (isClaimCreatedWithoutDocument) {
      return (
        <AppButton
          testId="claim-successfully-created_popup_upload-document-now"
          width="100%"
          onClick={handleUploadDocument}
        >
          {t('Upload Document Now')}
        </AppButton>
      );
    };

    return (
      <AppButton
        testId="claim-successfully-created_popup_go-to-expense-details"
        width="100%"
        onClick={handleGoToExpenseDetails}
      >
        {t('Go to Expense Details')}
      </AppButton>
    );
  }, [t, isClaimCreatedWithoutDocument, handleGoToExpenseDetails, handleUploadDocument]);

  useEffect(() => {
    return () => {
      if (!buttonClickedRef.current) {
        handleGoToExpenseDetails();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal
      testId="claim-successfully-created"
      visible
      onSetVisible={handleGoToExpenseDetails}
    >
      <Box pad={{ vertical: 'spacing24' }} align="center">
        <RoundDoneIcon size="48px" />

        <Text
          data-testId="claim-successfully-created_popup-title"
          margin={{ top: 'spacing12' }}
          size="2xl"
          weight={700}
        >
          {title}
        </Text>

        <Text
          data-testId="claim-successfully-created_popup-sub-title"
          margin={{ top: 'spacing12' }}
          textAlign="center"
        >
          {description}
        </Text>

        <Box
          data-testId="claim-successfully-created_popup-footer"
          margin={{ top: 'spacing24' }}
          direction="row"
          justify="center"
          width="100%"
          gap="spacing8"
        >
          <Box width={{ width: '100%', max: '210px' }}>
            {closeButton}
          </Box>

          <Box width={{ width: '100%', max: '210px' }}>
            {submitButton}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ClaimSuccessfullyCreatedPopup;
